<template>
  <nav class="bg-slate-900 border-b border-white/10 sticky top-0 z-50 backdrop-blur-xl bg-slate-900/80">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Logo -->
        <div class="flex items-center">
          <router-link to="/admin" class="flex items-center space-x-3 group">
            <div class="w-9 h-9 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20 group-hover:shadow-violet-500/40 transition-shadow">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span class="text-xl font-bold text-white tracking-tight">SJP Admin</span>
          </router-link>
        </div>
        
        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-6">
          <router-link
            to="/admin"
            class="nav-link"
            :class="{ 'active': $route.path === '/admin' }"
          >
            Dashboard
          </router-link>
          
          <router-link
            to="/admin/sessions"
            class="nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/session') }"
          >
            Sessions
          </router-link>
          
          <router-link
            to="/admin/candidates"
            class="nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/candidate') }"
          >
            Candidates
          </router-link>

          <router-link
            to="/admin/results"
            class="nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/result') }"
          >
            Results
          </router-link>

          <router-link
            to="/admin/voters"
            class="nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/voter') }"
          >
            Voters
          </router-link>

          <router-link
            to="/admin/committees"
            class="nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/committee') }"
          >
            Committees
          </router-link>
          
          <router-link
            to="/admin/audit"
            class="nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/audit') }"
          >
            Audit
          </router-link>

          <div class="w-px h-6 bg-white/10 mx-2"></div>

          <!-- User Menu -->
          <div class="relative" ref="userMenuRef">
            <button
              @click="showUserMenu = !showUserMenu"
              class="flex items-center space-x-3 text-slate-400 hover:text-white focus:outline-none transition-colors"
            >
              <div class="w-8 h-8 bg-slate-800 border border-white/10 rounded-full flex items-center justify-center shadow-inner">
                <span class="text-sm font-bold text-slate-300">
                  {{ userInitials }}
                </span>
              </div>
              <span class="text-sm font-medium">
                {{ authStore.userFullName }}
              </span>
              <svg class="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <!-- Dropdown -->
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showUserMenu"
                class="absolute right-0 mt-3 w-48 bg-slate-800 rounded-xl shadow-2xl border border-white/10 py-1 z-50 overflow-hidden"
              >
                <div class="px-4 py-3 border-b border-white/5 bg-slate-900/50">
                  <p class="text-sm font-semibold text-white">
                    {{ authStore.userFullName }}
                  </p>
                  <p class="text-xs text-slate-400 mt-0.5 truncate">
                    {{ authStore.user?.email }}
                  </p>
                </div>
                
                <button
                  @click="logout"
                  class="block w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </transition>
          </div>
        </div>
        
        <!-- Mobile menu button -->
        <div class="flex items-center md:hidden">
          <button
            @click="showMobileMenu = !showMobileMenu"
            class="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 focus:outline-none transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                v-if="!showMobileMenu"
                stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
              <path
                v-else
                stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Mobile menu -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div v-if="showMobileMenu" class="md:hidden border-t border-white/10 bg-slate-900">
        <div class="px-4 py-3 space-y-1">
          <router-link
            to="/admin"
            class="mobile-nav-link"
            :class="{ 'active': $route.path === '/admin' }"
            @click="showMobileMenu = false"
          >
            Dashboard
          </router-link>
          
          <router-link
            to="/admin/sessions"
            class="mobile-nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/session') }"
            @click="showMobileMenu = false"
          >
            Sessions
          </router-link>
          
          <router-link
            to="/admin/candidates"
            class="mobile-nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/candidate') }"
            @click="showMobileMenu = false"
          >
            Candidates
          </router-link>

          <router-link
            to="/admin/results"
            class="mobile-nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/result') }"
            @click="showMobileMenu = false"
          >
            Results
          </router-link>

          <router-link
            to="/admin/voters"
            class="mobile-nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/voter') }"
            @click="showMobileMenu = false"
          >
            Voters
          </router-link>

          <router-link
            to="/admin/committees"
            class="mobile-nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/committee') }"
            @click="showMobileMenu = false"
          >
            Committees
          </router-link>

          <router-link
            to="/admin/audit"
            class="mobile-nav-link"
            :class="{ 'active': $route.path.startsWith('/admin/audit') }"
            @click="showMobileMenu = false"
          >
            Audit Logs
          </router-link>
          
          <div class="border-t border-white/5 my-2 pt-2"></div>
          
          <div class="px-3 py-2 flex items-center space-x-3 mb-2">
            <div class="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center border border-white/10">
              <span class="text-sm font-bold text-slate-300">{{ userInitials }}</span>
            </div>
            <div>
              <p class="text-sm font-medium text-white">{{ authStore.userFullName }}</p>
              <p class="text-xs text-slate-400">{{ authStore.user?.email }}</p>
            </div>
          </div>

          <button
            @click="logout"
            class="mobile-nav-link text-red-400 hover:text-red-300 hover:bg-red-500/10 w-full text-left"
          >
            Sign Out
          </button>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'

const router = useRouter()
const authStore = useAuthStore()

const showUserMenu = ref(false)
const showMobileMenu = ref(false)
const userMenuRef = ref(null)

const userInitials = computed(() => {
  if (!authStore.user) return 'AD'
  const name = authStore.userFullName || 'Admin'
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
})

const logout = async () => {
  showUserMenu.value = false
  showMobileMenu.value = false
  authStore.logout()
  router.push('/admin/login')
}

// Close user menu when clicking outside
const handleClickOutside = (event) => {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.nav-link {
  @apply px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors relative;
}

.nav-link.active {
  @apply text-white;
}

.nav-link.active::after {
  content: '';
  @apply absolute -bottom-[19px] left-0 w-full h-[2px] bg-indigo-500 rounded-t-full shadow-[0_-2px_8px_rgba(99,102,241,0.8)];
}

.mobile-nav-link {
  @apply block px-3 py-2.5 rounded-lg text-base font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors;
}

.mobile-nav-link.active {
  @apply bg-indigo-500/10 text-indigo-300 border border-indigo-500/20;
}
</style>
